/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  Breadcrumbs,
  FormControl,
  Select,
  Typography,
  InputAdornment,
  IconButton,
  Grid
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api/index.js";
import authenticatedFetch from "../../../api/authenticatedFetch";
import camelize from "../../../utils/camelize.js";
import { Loader } from "../../../components/loader";
import { IndexTable } from "../../../components/index_table";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../user_management/styles";
import { MenuHeading } from "../../../components/menu_heading";
import { StatusContainer } from "./styles";
import { UpdateButton } from "../../../components/update_button";
import { datePickerStyles, filtersGridStyles, selectBaseStyles, viewTypographyStyles } from "../styles";
import { StyledInputLabel } from "../../restaurant/styles.jsx";
import dayjs from "dayjs";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import DropdownSearchBar from "../../../components/dorpdown_searchbar/index.jsx";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { ResetButton } from "../../../components/reset_button/index.jsx";
import { SelectedMenuItem } from '../../../components/selected_menu_item/index.jsx'
import ClearIcon from '@mui/icons-material/Clear';
import { capitalize } from 'lodash'
import { EmployeeCardAllocationLogsModal } from "../../../components/employee_card_allocation_logs_modal/index.jsx";
import { formatDate } from "../../../utils/formatDate.js";
import { useNotificationContext } from "../../../contexts/notificationContext.jsx";
import { buildQueryParams } from "../../../utils/buildQueryParams.js";
import { containsText, matchesNormalizedSearch } from "../../../utils/normalizeSearchTerm";
import { StyledMenuItem } from "../../../components/styled_menu_item/index.jsx";
import { hasSingleItem } from "../../../utils/hasSingleItem.js";
import { getSingleItemValue } from "../../../utils/getSingleItemValue.js";
import { Query_Keys } from "../../../lib/queryKeys.js";
import { RenderDiscountCardsList } from "../../../components/render_discount_cards_list/index.jsx";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  hasDisplayableDropdownValue,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeDiscountCard,
  normalizeEmployee,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  applyDateFilterChange,
  buildEmployeeDiscountQueryFilters,
  CUSTOM_DATE,
  DATE_FILTER_OPTIONS,
  isCustomDateRangeInvalid,
} from "../employee_discounts/employeeDiscountFilters";

const ALL_VALUE = 'All';
const ALL_RESTAURANTS_PLACEHOLDER = 'All restaurants';
const ALL_RESTAURANT_GROUPS_PLACEHOLDER = 'All restaurant groups';
const ALL_RESTAURANT_SUB_GROUPS_PLACEHOLDER = 'All sub-groups';
const defaultFilter = {
  restaurant_group_id: ALL_VALUE,
  restaurant_sub_group_id: ALL_VALUE,
  restaurant_ids: [ALL_VALUE],
  discount_card_ids: [ALL_VALUE],
  network_guest_name: ALL_VALUE,
  status: ALL_VALUE,
  dateFilter: ALL_VALUE,
  customDateFrom: null,
  customDateTo: null,
}

// Filters on action_date (updated_at); the range is read as Central days via the time_zone sent with it.
const getDateRangeParams = (sourceFilters) => {
  const { date_range: dateRange, time_zone: timeZone } = buildEmployeeDiscountQueryFilters(sourceFilters);
  return dateRange ? { date_range: dateRange, time_zone: timeZone } : {};
};

export const EmployeeDiscountCardSummary = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilter);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState("");
  const [discountCardSearch, setDiscountCardSearch] = useState("")
  const [statusSearch, setStatusSearch] = useState("")
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [selectedDiscountCardIds, setSelectedDiscountCardIds] = useState([ALL_VALUE])
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([])
  const [discountCardOptionsCache, setDiscountCardOptionsCache] = useState([])
  const [employeeOptionsCache, setEmployeeOptionsCache] = useState([])
  const [open, setOpen] = useState(false)
  const [selectedEmployeeRecord, setSelectedEmployeeRecord] = useState(null)
  const queryClient = useQueryClient()
  const [csvLoading, setCsvLoading] = useState(false)
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const showNotification = useNotificationContext();
  const dateRangeLabelId = React.useId();
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedDiscountCardSearch = useDebounce(discountCardSearch.trim(), 300)
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim(), 300)

  const hasGroupSelected = filters?.restaurant_group_id !== ALL_VALUE;

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const res = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id,
        restaurant_sub_group_id: filters?.restaurant_sub_group_id === ALL_VALUE ? undefined : filters?.restaurant_sub_group_id,
      }
    });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const fetchRestaurantSubGroups = async ({ pageParam = 1 }) => {
    if (!hasGroupSelected) return [];
    const response = await api.get("/v1/admin/dropdowns/restaurant_sub_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
        restaurant_group_id: filters?.restaurant_group_id,
      },
    });
    return response?.data?.map((subGroup) => ({
      id: subGroup.value,
      name: subGroup.label,
    })) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_sub_group_id: filters?.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const {
    data: restaurantSubGroups = [],
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
      source: "employee_discount_card_summary",
    }),
    queryFn: fetchRestaurantSubGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: hasGroupSelected,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchDiscountCardsList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/discount_card_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedDiscountCardSearch || undefined,
        restaurant_id: filters?.restaurant_ids?.includes(ALL_VALUE) ? undefined : filters?.restaurant_ids,
        restaurant_group_id: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id
      }
    });
    return response?.data?.map(normalizeDiscountCard) || []
  };

  const {
    data: discountCards = [],
    isFetching: discountCardFetching,
    isFetchingNextPage: discountCardsFetchingNextPage,
    fetchNextPage: fetchNextDiscountCardsPage,
    hasNextPage: hasNextDiscountCardsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.discount_cards_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_id: filters?.restaurant_ids,
      search: debouncedDiscountCardSearch,
    }),
    queryFn: fetchDiscountCardsList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchEmployeesList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/active_guests_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedEmployeeSearch || undefined,
        restaurant_id: filters?.restaurant_ids?.includes(ALL_VALUE) ? undefined : filters?.restaurant_ids,
        restaurant_group_id: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id
      }
    });
    return response?.data
      ?.map(normalizeEmployee)
      ?.filter((employee) => (
        hasDisplayableDropdownValue(employee?.label)
        && hasDisplayableDropdownValue(employee?.value)
      )) || [];
  };

  const {
    data: employees = [],
    isFetching: employeeFetching,
    isFetchingNextPage: employeesFetchingNextPage,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.active_guests_list({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_id: filters?.restaurant_ids,
      search: debouncedEmployeeSearch,
    }),
    queryFn: fetchEmployeesList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
  });

  const { rootRef: discountCardMenuRef, targetRef: discountCardLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextDiscountCardsPage,
    hasNextPage: hasNextDiscountCardsPage,
    isFetchingNextPage: discountCardsFetchingNextPage,
  });

  const { rootRef: employeeMenuRef, targetRef: employeeLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
    isFetchingNextPage: employeesFetchingNextPage,
  });

  const fetchEmployeeDiscountReport = async () => {
    const sourceFilters = appliedFilters || defaultFilter;
    const apiParams  = {
      per_page: itemsPerPage,
      page: page
    }

    if (sourceFilters?.restaurant_group_id !== ALL_VALUE) {
      apiParams.restaurant_group_ids = sourceFilters?.restaurant_group_id;
    }

    if (sourceFilters?.restaurant_sub_group_id !== ALL_VALUE) {
      apiParams.restaurant_sub_group_id = sourceFilters?.restaurant_sub_group_id;
    }

    if (!sourceFilters?.restaurant_ids?.includes(ALL_VALUE)) {
      apiParams.restaurant_ids = sourceFilters?.restaurant_ids;
    }

    if (sourceFilters?.network_guest_name !== ALL_VALUE) {
      apiParams.network_guest_name = getEmployeeName(sourceFilters?.network_guest_name);
    }

    if (sourceFilters?.status !== ALL_VALUE) {
      apiParams.status = sourceFilters?.status;
    }

    if (!sourceFilters?.discount_card_ids?.includes(ALL_VALUE)) {
      apiParams.discount_card_ids = sourceFilters?.discount_card_ids;
    }

    Object.assign(apiParams, getDateRangeParams(sourceFilters));

    if (sortBy) {
      apiParams.sort_by = sortBy;
      apiParams.sort_order = sortOrder;
    }

    const response = await api.get("/v1/admin/reports/network_guest_discounts", {
      params: apiParams,
    });

    const result = {
      data: response?.data,
      pageCount: response?.headers["total-count"]
    }
    return result
  }

  const { data: queryResult, isFetching } = useQuery({
    queryKey: Query_Keys.employee_discount_report({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchEmployeeDiscountReport,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const data = queryResult?.data;

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurants, 'id', ['name']));
  }, [restaurants]);

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantGroups, 'label', ['value']));
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantSubGroups, 'id', ['name']));
  }, [restaurantSubGroups]);

  useEffect(() => {
    setDiscountCardOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, discountCards, 'id', ['name']));
  }, [discountCards]);

  useEffect(() => {
    setEmployeeOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, employees, 'value', ['label']));
  }, [employees]);

  const hasSingleAvailableDiscountCard = !debouncedDiscountCardSearch && hasSingleItem(discountCards);
  const hasSingleAvailableEmployee = !debouncedEmployeeSearch && hasSingleItem(employees);

  useEffect(() => {
    if (debouncedRestaurantGroupSearch) return;
    if (hasNextRestaurantGroupsPage) return;
    if (filters.restaurant_group_id !== ALL_VALUE) return;
    if (!hasSingleItem(restaurantGroups)) return;

    const singleGroupId = getSingleItemValue(restaurantGroups)?.label;
    setFilters((prev) => ({ ...prev, restaurant_group_id: singleGroupId }));
  }, [debouncedRestaurantGroupSearch, hasNextRestaurantGroupsPage, restaurantGroups]);

  useEffect(() => {
    if (debouncedRestaurantSearch) return;
    if (hasNextRestaurantsPage) return;
    if (!filters.restaurant_ids.every((id) => id === ALL_VALUE)) return;
    if (!hasSingleItem(restaurants)) return;
    if (restaurantFetching) return

    const singleRestaurantId = getSingleItemValue(restaurants)?.id;
    setFilters((prev) => ({
      ...prev,
      restaurant_ids: [singleRestaurantId],
      network_guest_name: ALL_VALUE,
      discount_card_ids: [ALL_VALUE],
    }));
    setSelectedDiscountCardIds([ALL_VALUE]);
  }, [debouncedRestaurantSearch, hasNextRestaurantsPage, restaurants, restaurantFetching]);

  useEffect(() => {
    if (hasSingleAvailableEmployee) {
      const singleEmployee = getSingleItemValue(employees);
      setFilters(prev => ({
        ...prev,
        network_guest_name: singleEmployee.value,
      }));
    }
  }, [employees, hasSingleAvailableEmployee]);

  useEffect(() => {
    if (hasSingleAvailableDiscountCard) {
      const singleDiscountCard = getSingleItemValue(discountCards);
      setFilters(prev => ({
        ...prev,
        discount_card_ids: [singleDiscountCard.id],
      }));
      setSelectedDiscountCardIds([singleDiscountCard.id]);
    }
  }, [discountCards, hasSingleAvailableDiscountCard]);

  const handleEmployeeChange = (event) => {
    setFilters({ ...filters, network_guest_name: event.target.value });
    setFiltersChanged(true);
  }

  const handleEmployeeClear = () => {
    setFilters({ ...filters, network_guest_name: ALL_VALUE });
    setFiltersChanged(true);
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedEmployeeRecord(null)
  }

  const handleSelectedEmployeeRecord = useCallback((row) => {
    setSelectedEmployeeRecord(row?.sourceRecord ?? null)
  }, [])

  const handleRestaurantGroupChange = (event) => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters({
      ...filters,
      restaurant_group_id: event.target.value,
      restaurant_sub_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE],
      discount_card_ids: [ALL_VALUE],
      network_guest_name: ALL_VALUE,
    });
    setSelectedDiscountCardIds([ALL_VALUE])
    setFiltersChanged(true);
  };

  const handleRestaurantSubGroupChange = (event) => {
    setRestaurantSearch("");
    setFilters({
      ...filters,
      restaurant_sub_group_id: event.target.value,
      restaurant_ids: [ALL_VALUE],
      discount_card_ids: [ALL_VALUE],
      network_guest_name: ALL_VALUE,
    });
    setSelectedDiscountCardIds([ALL_VALUE])
    setFiltersChanged(true);
  };

  const handleRestaurantGroupClear = () => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters({ ...filters, restaurant_group_id: ALL_VALUE, restaurant_sub_group_id: ALL_VALUE, restaurant_ids: [ALL_VALUE], discount_card_ids: [ALL_VALUE], network_guest_name: ALL_VALUE })
    setSelectedDiscountCardIds([ALL_VALUE])
    setFiltersChanged(true);
  }

  const handleRestaurantSubGroupClear = () => {
    setRestaurantSearch("");
    setFilters({ ...filters, restaurant_sub_group_id: ALL_VALUE, restaurant_ids: [ALL_VALUE], discount_card_ids: [ALL_VALUE], network_guest_name: ALL_VALUE })
    setSelectedDiscountCardIds([ALL_VALUE])
    setFiltersChanged(true);
  }

  const handleRestaurantDropdownChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltersChanged(true);

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters({ ...filters, restaurant_ids: [ALL_VALUE], discount_card_ids: [ALL_VALUE], network_guest_name: ALL_VALUE });
    }
    else {
      const newRestaurantIds = value.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_ids: newRestaurantIds, discount_card_ids: [ALL_VALUE], network_guest_name: ALL_VALUE });
    }
    setSelectedDiscountCardIds([ALL_VALUE])
  }

  const handleRestaurantClear = () => {
    setFilters({ ...filters, restaurant_ids: [ALL_VALUE], discount_card_ids: [ALL_VALUE], network_guest_name: ALL_VALUE })
    setSelectedDiscountCardIds([ALL_VALUE])
    setFiltersChanged(true);
  }

  const handleCardTypeChange = (event) => {
    const values = event.target.value;

    if ((values?.length === 1 && values.includes(ALL_VALUE)) || values?.length === 0) {
      setFilters({ ...filters, discount_card_ids: [ALL_VALUE] });
      setSelectedDiscountCardIds([ALL_VALUE])
    }
    else {
      const newDiscountCardIds = values.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, discount_card_ids: newDiscountCardIds });
      setSelectedDiscountCardIds(newDiscountCardIds)
    }
    setFiltersChanged(true);
  };

  const handleDiscountCardClear = () => {
    setFilters({ ...filters, discount_card_ids: [ALL_VALUE] })
    setSelectedDiscountCardIds([ALL_VALUE])
    setFiltersChanged(true);
  }

  const handleStatusChange = (event) => {
    setFilters({ ...filters, status: event.target.value });
    setFiltersChanged(true);
  };

  const handleStatusClear = () => {
    setFilters({ ...filters, status: ALL_VALUE });
    setFiltersChanged(true);
  }

  const handleDateChange = useCallback((field, value) => {
    setFilters((prev) => applyDateFilterChange(prev, field, value));
    setFiltersChanged(true);
  }, []);

  const handleDateClear = () => {
    setFilters({ ...filters, dateFilter: ALL_VALUE, customDateFrom: null, customDateTo: null });
    setFiltersChanged(true);
  }

  const isUpdateDisabled = isCustomDateRangeInvalid(filters);

  const handleUpdate = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setItemsPerPage(25);
    setPage(1);
  };

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setFilters(defaultFilter)
    setItemsPerPage(25);
    setPage(1);
    setSelectedDiscountCardIds([ALL_VALUE])
  }

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurant_ids || filters?.restaurant_ids?.length === 0) {
      return ALL_RESTAURANTS_PLACEHOLDER;
    }

    if (filters?.restaurant_ids?.includes(ALL_VALUE)) {
      return ALL_RESTAURANTS_PLACEHOLDER;
    }

    if (filters?.restaurant_ids?.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(r => r.id === filters.restaurant_ids[0]);
      return restaurant ? restaurant.name : ALL_RESTAURANTS_PLACEHOLDER;
    }

    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  }

  const getDiscountCardDisplayValue = () => {
    if (!filters?.discount_card_ids || filters?.discount_card_ids?.length === 0) {
      return 'All';
    }

    if (filters?.discount_card_ids?.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters?.discount_card_ids?.length === 1 && discountCardOptionsCache) {
      const discountCard = discountCardOptionsCache.find(r => r.id === filters.discount_card_ids[0]);
      return discountCard ? discountCard.name : 'All';
    }

    return `${filters?.discount_card_ids?.length} Perq Cards Selected`;
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      // Export what the table shows: the applied filters, not unsaved selections.
      const sourceFilters = appliedFilters || defaultFilter;
      const { dateFilter, customDateFrom, customDateTo, ...csvFilters } = sourceFilters;
      Object.assign(csvFilters, getDateRangeParams(sourceFilters));
      if (csvFilters.restaurant_group_id === ALL_VALUE) {
        delete csvFilters.restaurant_group_id;
      }
      if (csvFilters.restaurant_sub_group_id === ALL_VALUE) {
        delete csvFilters.restaurant_sub_group_id;
      }
      if (csvFilters.restaurant_ids?.includes(ALL_VALUE)) {
        delete csvFilters.restaurant_ids;
      }
      if (csvFilters.network_guest_name === ALL_VALUE) {
        delete csvFilters.network_guest_name;
      } else {
        csvFilters.network_guest_name = getEmployeeName(csvFilters.network_guest_name);
      }
      if (csvFilters.discount_card_ids?.includes(ALL_VALUE)) {
        delete csvFilters.discount_card_ids;
      }
      if (csvFilters.status === ALL_VALUE) {
        delete csvFilters.status;
      }

      const query = buildQueryParams({ ...csvFilters, restaurant_group_ids: csvFilters?.restaurant_group_id })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=network_guest_discounts_summary&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `employee_discount_card_summary_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const statuses = [
    { label: "Active", value: "active" },
    { label: "Revoked", value: "revoked" },
  ];

  const filteredStatus = statuses?.filter((status) => {
    return matchesNormalizedSearch(status?.label, statusSearch);
  })

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${filters.restaurant_group_id}`,
  );
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], "label")
      : restaurantGroups;

  const selectedRestaurantSubGroupOption = restaurantSubGroupOptionsCache?.find(
    (subGroup) => `${subGroup?.id}` === `${filters.restaurant_sub_group_id}`,
  );
  const displayedRestaurantSubGroups = restaurantSubGroupSearch.trim()
    ? restaurantSubGroups
    : selectedRestaurantSubGroupOption
      ? mergeUniqueByKey(restaurantSubGroups, [selectedRestaurantSubGroupOption], "id")
      : restaurantSubGroups;

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) =>
    filters?.restaurant_ids?.some((id) => `${id}` === `${restaurant?.id}`),
  );
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, "id");

  const getRestaurantGroupName = (id) => {
    if (id === ALL_VALUE) return ALL_RESTAURANT_GROUPS_PLACEHOLDER;
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup ? restaurantGroup?.value : ALL_RESTAURANT_GROUPS_PLACEHOLDER
  }

  const getRestaurantSubGroupName = (id) => {
    if (id === ALL_VALUE) return ALL_RESTAURANT_SUB_GROUPS_PLACEHOLDER;
    const restaurantSubGroup = restaurantSubGroupOptionsCache?.find((res) => `${res.id}` === `${id}`)
    return restaurantSubGroup ? restaurantSubGroup?.name : ALL_RESTAURANT_SUB_GROUPS_PLACEHOLDER
  }

  const getEmployeeDisplayValue = () => {
    if (hasSingleAvailableEmployee) {
      return getSingleItemValue(employees)?.label || 'All';
    }

    if (filters?.network_guest_name === ALL_VALUE) {
      return 'All';
    }

    return getEmployeeName(filters?.network_guest_name);
  }

  const getEmployeeName = (employeeId) => {
    if (!employeeId || employeeId === ALL_VALUE) {
      return 'All';
    }

    const employee = employeeOptionsCache?.find((option) => `${option?.value}` === `${employeeId}`)
      || employees?.find((option) => `${option?.value}` === `${employeeId}`);

    return employee?.label || employeeId;
  }

  const tableColumns = [
    "employee_phone",
    "action_date",
    "name",
    "restaurant_group",
    "restaurant_sub_group",
    "restaurant",
    "card_type",
    "status",
    "last_performed_by",
  ];

  const columns = tableColumns?.map((column) => {
    return {
      key: column,
      label: camelize(column),
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      const restaurantGroupName = record?.restaurant_group?.name;
      const restaurantSubGroupName = record?.restaurant_sub_group?.name;

      return {
        sourceRecord: record,
        employee_phone: record?.network_guest?.phone_no,
        action_date: formatDate(record?.action_date, record?.time_zone_abbr),
        name: record?.network_guest?.full_name,
        restaurant_group: restaurantGroupName || '-',
        restaurant_sub_group: restaurantSubGroupName || '-',
        restaurant: record?.restaurant?.name || '-',
        card_type: {
          display: (
            <RenderDiscountCardsList 
              data={record?.discount_card ? [record.discount_card] : []} 
              disablePopover={true} 
            />
          ),
          sortValue: record?.discount_card?.name
        },
        status: {
          display: (
            <StatusContainer status={record?.status}>
              {camelize(record?.status)}
            </StatusContainer>
          ),
          sortValue: record?.status
        },
        last_performed_by: record?.last_performed_by || '-',
      };
    });
  };

  const rows = TableRows(data);

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Perq Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Perq Card Allocation</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <Grid
          container
          spacing={1}
          alignItems="center"
          sx={{ ...filtersGridStyles, paddingTop: '0px' }}
          data-testid="orders-modifiers-filter-section"
        >
          <Grid item xs>
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                gap: '18px'
              }}
            >
              <Box sx={{ display: "flex", gap: "18px", flexWrap: 'wrap', paddingTop: '0px' }}>
                <Typography variant="body2" sx={{...viewTypographyStyles, mt: 1}}>
                  View
                </Typography>
                <FormControl size="small">
                  <StyledInputLabel id={dateRangeLabelId}>Date Range</StyledInputLabel>
                  <Select
                    labelId={dateRangeLabelId}
                    value={filters?.dateFilter || ALL_VALUE}
                    renderValue={() => filters?.dateFilter}
                    label="Date Range"
                    size="small"
                    data-testid="employee-card-allocation-date-filter"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { sx: { width: "230px" } },
                      autoFocus: false,
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 160,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={(e) => handleDateChange("dateFilter", e.target.value)}
                    endAdornment={
                      filters?.dateFilter !== ALL_VALUE ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            aria-label="Clear date range"
                            onClick={handleDateClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    {/* All option */}
                    <StyledMenuItem value={ALL_VALUE}>
                      {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    {DATE_FILTER_OPTIONS.map((value) => (
                      <StyledMenuItem value={value} key={value}>
                        {filters?.dateFilter === value ? <SelectedMenuItem data={value} /> : value}
                      </StyledMenuItem>
                    ))}
                  </Select>
                </FormControl>
                {filters?.dateFilter === CUSTOM_DATE && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        Days
                      </Typography>
                      <DatePicker
                        format="MM-DD-YYYY"
                        sx={{ ...datePickerStyles, width: { xs: "100%", sm: "140px", lg: "160px" } }}
                        slotProps={{ textField: { size: "small", inputProps: { "aria-label": "Date range from" } } }}
                        value={filters.customDateFrom || null}
                        onChange={(date) => handleDateChange("customDateFrom", date)}
                      />
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        through
                      </Typography>
                      <DatePicker
                        format="MM-DD-YYYY"
                        sx={{ ...datePickerStyles, width: { xs: "100%", sm: "140px", lg: "160px" } }}
                        slotProps={{ textField: { size: "small", inputProps: { "aria-label": "Date range to" } } }}
                        value={filters.customDateTo || null}
                        onChange={(date) => handleDateChange("customDateTo", date)}
                      />
                    </LocalizationProvider>
                  </Box>
                )}

                <FormControl size="small">
                  <StyledInputLabel>Restaurant Group</StyledInputLabel>
                  <Select
                    value={filters?.restaurant_group_id || ALL_VALUE}
                    renderValue={() => getRestaurantGroupName(filters?.restaurant_group_id)}
                    label="Restaurant Group"
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 120,
                      width: 160,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantGroupChange}
                    onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                    endAdornment={
                      hasGroupSelected ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                    {
                      displayedRestaurantGroups
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.label}
                            value={res?.label}
                          >
                            {
                              filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupsPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl size="small" disabled={!hasGroupSelected}>
                  <StyledInputLabel>Restaurant Sub Group</StyledInputLabel>
                  <Select
                    value={filters?.restaurant_sub_group_id || ALL_VALUE}
                    label="Restaurant Sub Group"
                    renderValue={() => getRestaurantSubGroupName(filters?.restaurant_sub_group_id)}
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantSubGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 160,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantSubGroupChange}
                    onClose={() => setTimeout(() => setRestaurantSubGroupSearch(''), 1000)}
                    endAdornment={
                      filters?.restaurant_sub_group_id !== ALL_VALUE ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantSubGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSubGroupSearch} placeholder={'Search restaurant sub group'} isFetching={restaurantSubGroupFetching} />
                    {
                      displayedRestaurantSubGroups
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.id}
                            value={res?.id}
                          >
                            {
                              `${filters?.restaurant_sub_group_id}` === `${res?.id}` ? <SelectedMenuItem data={res?.name} /> : res?.name
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantSubGroupsPage}
                      targetRef={restaurantSubGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <StyledInputLabel>Restaurant</StyledInputLabel>
                  <Select
                    value={filters?.restaurant_ids || [ALL_VALUE]}
                    label="Restaurant"
                    renderValue={() => getRestaurantDisplayValue()}
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 170,
                      borderColor: "#2b4fb9",
                    }}
                    multiple
                    onChange={handleRestaurantDropdownChange}
                    onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                    data-testid='restaurant-restaurant-group-select'
                    endAdornment={
                      !filters?.restaurant_ids?.includes(ALL_VALUE) ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 22 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                    {
                      displayedRestaurants
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.id}
                            value={res?.id}
                          >
                            {
                              filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantsPage}
                      targetRef={restaurantLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <StyledInputLabel>Card Type</StyledInputLabel>
                  <Select
                    multiple
                    label="Card Type"
                    value={hasSingleAvailableDiscountCard
                      ? [getSingleItemValue(discountCards)?.id]
                      : (selectedDiscountCardIds || [ALL_VALUE])}
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: discountCardMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 120,
                      width: 140,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleCardTypeChange}
                    onClose={() => setTimeout(() => setDiscountCardSearch(''), 1000)}
                    renderValue={() => {
                      if (hasSingleAvailableDiscountCard) {
                        return getSingleItemValue(discountCards)?.name || 'All';
                      }
                      return getDiscountCardDisplayValue();
                    }}
                    endAdornment={
                      !selectedDiscountCardIds?.includes(ALL_VALUE) && !hasSingleAvailableDiscountCard ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleDiscountCardClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    {discountCards?.length === 0 && (
                      <StyledMenuItem
                        disabled
                        value=""
                      >
                        <em>No perq cards available</em>
                      </StyledMenuItem>
                    )}
                    <DropdownSearchBar changeSearchText={setDiscountCardSearch} placeholder={'Search perq card'} isFetching={discountCardFetching} />
                    {/* All option */}
                    {!hasSingleAvailableDiscountCard && (<StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                      disabled={!filters?.discount_card_ids?.includes(ALL_VALUE)}
                    >
                      {filters?.discount_card_ids?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {
                      discountCards
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.id}
                            value={res?.id}
                          >
                            {
                              hasSingleAvailableDiscountCard || selectedDiscountCardIds?.includes(res?.id) ? (
                                <SelectedMenuItem data={res?.name} />
                              ) : (
                                <>
                                  {res?.name}
                                </>
                              )
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextDiscountCardsPage}
                      targetRef={discountCardLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <StyledInputLabel>Status</StyledInputLabel>
                  <Select
                    value={filters?.status || ALL_VALUE}
                    renderValue={() =>
                      capitalize(filters?.status)
                    }
                    size="small"
                    label="Status"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 120,
                      width: 140,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleStatusChange}
                    onClose={() => setTimeout(() => setStatusSearch(''), 1000)}
                    endAdornment={
                      filters?.status !== ALL_VALUE ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleStatusClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setStatusSearch} placeholder={'Search status'} />
                    {/* All option */}
                    <StyledMenuItem
                      value={ALL_VALUE}
                    >
                      {filters?.status === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    {
                      filteredStatus
                        ?.filter((status) =>
                          containsText(
                            `${status?.label}`,
                            statusSearch
                          )
                        )
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.value}
                            value={res?.value}
                          >
                            {
                              filters?.status === res?.value ? <SelectedMenuItem data={res?.label} /> : res?.label
                            }
                          </StyledMenuItem>
                        ))
                    }
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <StyledInputLabel>Employee Name</StyledInputLabel>
                  <Select
                    value={
                      hasSingleAvailableEmployee
                      ? getSingleItemValue(employees)?.value
                      : (filters?.network_guest_name || ALL_VALUE)
                    }
                    renderValue={() => {
                      return getEmployeeDisplayValue();
                    }}
                    label="Employee Name"
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: employeeMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 120,
                      width: 160,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleEmployeeChange}
                    onClose={() => setEmployeeSearch("")}
                    endAdornment={
                      filters?.network_guest_name !== ALL_VALUE && !hasSingleAvailableEmployee ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleEmployeeClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setEmployeeSearch} placeholder={'Search employee'} isFetching={employeeFetching} />
                    {/* All option */}
                    {!hasSingleAvailableEmployee && (<StyledMenuItem
                      value={ALL_VALUE}
                    >
                      {filters?.network_guest_name === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {employees
                      .map((employee) => (
                        <StyledMenuItem
                          key={employee.value}
                          value={employee.value}
                        >
                          {
                            hasSingleAvailableEmployee || filters?.network_guest_name === employee.value ? <SelectedMenuItem data={employee?.label} /> : employee?.label
                          }
                        </StyledMenuItem>
                      ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextEmployeesPage}
                      targetRef={employeeLoaderRef}
                    />
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs="auto" sx={{gap: '10px'}}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                status={isFetching}
              />

              <UpdateButton
                onClick={handleUpdate}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Grid>
        </Grid>

        <MenuHeading report={false}>Employee Perq Card Allocation Report</MenuHeading>

        {isFetching ? (
          <Loader />
        ) : (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="discount-card-summary"
            type="entries"
            sortMode="server"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={queryResult?.pageCount}
            report={false}
            enableRowClick={true}
            handleOpen={handleOpen}
            setSelectedRow={handleSelectedEmployeeRecord}
            showDownloadButton={true}
            disableDownloadButton={data?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        )}

        {/* Employee Perq Card Allocation Logs Modal */}
        <EmployeeCardAllocationLogsModal
          open={open}
          handleClose={handleClose}
          selectedRecord={selectedEmployeeRecord}
        />
      </Box>
    </React.Fragment>
  );
};
